import { Switch } from '@headlessui/react';
import { cn, NumericInput, NumericInputProps, UnsavedChangesDialog } from '..';
import { isNullUndefinedOrEmpty } from '@shared';
import classNames from 'classnames';
import React from 'react';
import { DialogModal } from './DialogModal';
import { Button } from './ui/button';

export type FundingSourceFormProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  subTitle: string;
  children: React.ReactNode;
  handleSubmit: () => void;
  formIsValid: boolean | undefined;
  formErrorMessage: React.ReactNode;
  showUnsavedChangesDialog: boolean;
  hideSubmitButton?: boolean;
  handleCancelClose: () => void;
  handleConfirmClose: () => void;
};

export function FundingSourceForm({
  open,
  onClose,
  title,
  subTitle,
  children,
  handleSubmit,
  formIsValid,
  formErrorMessage,
  showUnsavedChangesDialog,
  handleCancelClose,
  handleConfirmClose,
  hideSubmitButton,
}: FundingSourceFormProps) {
  const modalContent = (
    <>
      <div className="mt-2 flex flex-col gap-2">
        {children}
        {!formIsValid && formErrorMessage}
      </div>

      <UnsavedChangesDialog
        showWarning={showUnsavedChangesDialog}
        handleCancelClose={handleCancelClose}
        handleConfirmClose={handleConfirmClose}
      />
    </>
  );

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title={title}
      subTitle={subTitle}
      width="lg"
      primaryButton={
        !hideSubmitButton && (
          <Button
            className="mb-14 px-12 text-lg font-semibold  sm:mb-0 sm:ml-3 sm:w-auto"
            onClick={handleSubmit}
          >
            Submit
          </Button>
        )
      }
    >
      {modalContent}
    </DialogModal>
  );
}

type FormFieldProps = {
  title: string | React.ReactNode;
  description: string;
  example?: string;
  isLastField?: boolean;
};

type FormFieldContainerProps = FormFieldProps & {
  children: React.ReactNode;
  className?: string;
};

FundingSourceForm.FieldContainer = function FundingSourceFormFieldContainer({
  title,
  description,
  example,
  children,
  isLastField,
  className,
}: FormFieldContainerProps) {
  return (
    <div
      className={`grid grid-cols-2 gap-x-6 gap-y-2 py-7 ${isLastField ? '' : 'border-b'}`}
    >
      <div className="col-start-1 col-end-3 flex flex-col gap-2 pb-2 ">
        {typeof title === 'string' ? (
          <h2 className="text-lg leading-5 text-gray-800">{title}</h2>
        ) : (
          <div className="text-lg leading-5 text-gray-800">{title}</div>
        )}
        <p className="text-sm text-gray-700 md:text-base">{description}</p>
        {example && (
          <p className="text-xs italic text-gray-700 md:text-sm">{example}</p>
        )}
      </div>
      {React.Children.map(children, (child, index) => (
        <div
          className={cn(
            'col-start-1 col-end-4',
            index % 2 === 1
              ? 'md:col-start-2 md:col-end-4'
              : 'md:col-start-1 md:col-end-2',
            className,
          )}
        >
          {child}
        </div>
      ))}
    </div>
  );
};

type NumericFormFieldProps = {
  InputField: typeof NumericInput;
  inputProps: NumericInputProps;
  fieldError: boolean | undefined;
};

FundingSourceForm.NumericField = function FundingSourceNumericField({
  InputField,
  inputProps,
  fieldError,
}: NumericFormFieldProps) {
  return (
    <>
      <InputField {...inputProps} />
      <RequiredFieldErrorMessage
        fieldValue={inputProps.value}
        fieldError={fieldError}
      />
    </>
  );
};

type BooleanFormFieldProps = {
  changeHandler: (value: boolean) => void;
  value: boolean;
  enabledLabel: string;
  disabledLabel: string;
  disabled?: boolean;
};

FundingSourceForm.BooleanToggleField = function FundingSourceBooleanField({
  changeHandler,
  value,
  enabledLabel,
  disabledLabel,
  disabled,
}: BooleanFormFieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <h2 className="text-xs font-bold leading-[14px] text-[#454446]">
        <span className={value ? '' : 'text-purple underline'}>
          {disabledLabel}{' '}
        </span>
        /{' '}
        <span className={value ? 'text-purple underline' : ''}>
          {' '}
          {enabledLabel}{' '}
        </span>
      </h2>
      <div className="mt-3 flex">
        <FundingSourceToggle
          enabled={value}
          setEnabled={changeHandler}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

type RequiredFieldProps = {
  fieldValue: number | null;
  fieldError: boolean | undefined;
};

function RequiredFieldErrorMessage({
  fieldValue,
  fieldError,
}: RequiredFieldProps) {
  if (fieldError && isNullUndefinedOrEmpty(fieldValue)) {
    return <p className="ml-2 text-sm text-red-400">Required field</p>;
  }
  return null;
}

interface ToggleProps {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  disabled?: boolean;
}

export function FundingSourceToggle(props: ToggleProps) {
  return (
    <Switch
      disabled={props.disabled ? true : false}
      checked={props.enabled}
      onChange={props.setEnabled}
      className={classNames(
        props.enabled ? 'bg-purple' : 'bg-gray-200',
        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1',
      )}
    >
      <span className="sr-only">Use setting</span>
      <span
        aria-hidden="true"
        className={classNames(
          props.enabled ? 'translate-x-5' : 'translate-x-0',
          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
        )}
      />
    </Switch>
  );
}

export function setFundingSourceStateWithChangedFlag<T>(
  setState: React.Dispatch<React.SetStateAction<T>>,
  setHasChangedField: React.Dispatch<React.SetStateAction<boolean>>,
) {
  return (newState: React.SetStateAction<T>) => {
    setState(prevState => {
      const updatedState =
        typeof newState === 'function'
          ? (newState as (prevState: T) => T)(prevState)
          : newState;
      setHasChangedField(true);
      return updatedState;
    });
  };
}
